import { model as geoblock } from '!/launch'
import { model as router } from '!/router'
import { model as session } from '$/session'
import { model as config, remote } from '@/config'
import { GLOBAL_REGION, LocalStorageKey, PATH } from '@/constants'
import {
  F,
  T,
  aye,
  extractWrappedId,
  getPrimaryRegionIdFromStorage,
  getQueryParamsFromUrl,
  nay,
  normalizeForComparison,
} from '@/helpers'
import { api } from '@setplex/tria-api'
import { attach, createEvent, createStore, sample, type Store } from 'effector'
import { persist } from 'effector-storage/local'
import { and, not, pending } from 'patronum'
import { resetContentOnPreferredRegionChange } from '~/entities/content'
import { preferencesEntity } from '~/entities/preferences'
import { resetTvshowsOnPreferredRegionChange } from '~/entities/tvshows/events'
import { profile } from '~/features/profile'
import { selectEpgRegionModel } from '../epg/regions/select'
import { landingRegionFeatureModel } from '../landingRegion'
import { noRegionSelection } from './constants'
import type { IPreferenceStorage, IPrimaryRegion } from './index.h'

export const init = createEvent()

export const $isDataReady = createStore(false)
export const $isOnboardingEnabled = createStore(false)
// true once the onboarding flag has been determined (config ready + geoblock known),
// regardless of whether onboarding ended up enabled or disabled
export const $isOnboardingResolved = createStore(false)

// Firebase flag value
export const $enabled: Store<boolean> = config.get(
  remote.uvo_isOnboardingEnabled
)

// Auth is resolved via getInfo; pending tracks getInfo + profile for onboarding gate
export const $inProcess = pending([
  api.userSession.getInfoFx,
  profile.getProfileFx,
])

sample({
  clock: $inProcess,
  fn: nay,
  target: $isDataReady,
})

sample({
  clock: [config.$ready, $enabled, geoblock.$isGeoblock],
  source: and($enabled, not(geoblock.$isGeoblock)),
  filter: config.$ready,
  fn: aye,
  target: $isOnboardingEnabled,
})

// mark onboarding data as resolved once config is ready and the sample above
// has had a chance to compute the final value of $isOnboardingEnabled
sample({
  clock: [config.$ready, $enabled, geoblock.$isGeoblock],
  filter: config.$ready,
  fn: T,
  target: $isOnboardingResolved,
})

export const openPreferencesPopup = createEvent()
export const closePreferencesPopup = createEvent()

export const $isPreferencesPopupOpened = createStore(false) //
  .on(openPreferencesPopup, T)
  .on(closePreferencesPopup, F)

export const setTryOpeningPopupToTrue = createEvent()
const resetShouldTryToOpenPopup = createEvent()
export const $shouldTryToOpenPopup = createStore(false)
  .on(setTryOpeningPopupToTrue, T)
  .on(openPreferencesPopup, F)
  .reset(resetShouldTryToOpenPopup)

export const setPreferenceShouldBeUpdatedFromInfoTrue = createEvent()
const resetPreferenceShouldBeUpdatedFromInfo = createEvent()
export const $preferenceShouldBeUpdatedFromInfo = createStore(false)
  .on(setPreferenceShouldBeUpdatedFromInfoTrue, T)
  .reset(resetPreferenceShouldBeUpdatedFromInfo)

export const setPreference = createEvent<IPrimaryRegion>()
export const setPreferenceWithoutUtm = createEvent<IPrimaryRegion>()

export const $preferenceStorage = createStore<IPreferenceStorage>({
  primaryRegion: null,
})
  .on(setPreference, (_, primaryRegion) => {
    // If there is id in utm_campaign, set/rewrite it to primaryRegion (even user`s profile region from BE)
    const { utm_custom_param } = getQueryParamsFromUrl()
    const id = extractWrappedId(utm_custom_param ?? '')

    if (id && !window.location.pathname.startsWith(PATH.SETTINGS)) {
      return { primaryRegion: { id, name: '' } }
    }

    return { primaryRegion }
  })
  .on(setPreferenceWithoutUtm, (_, primaryRegion) => {
    return { primaryRegion }
  })

sample({
  clock: [session.$info, $preferenceShouldBeUpdatedFromInfo],
  source: {
    info: session.$info,
    preferenceShouldBeUpdatedFromInfo: $preferenceShouldBeUpdatedFromInfo,
  },
  filter: ({ info, preferenceShouldBeUpdatedFromInfo }) => {
    return Boolean(info) && preferenceShouldBeUpdatedFromInfo
  },
  fn: ({ info }) => {
    return info?.primaryRegionObject
  },
  target: [setPreferenceWithoutUtm, resetPreferenceShouldBeUpdatedFromInfo],
})

persist({
  store: $preferenceStorage,
  key: LocalStorageKey.PrimaryRegion,
})

export const $preference = $preferenceStorage.map(
  (storage) => storage?.primaryRegion ?? null
)

export const regionChanged = createEvent()

const $previousPreferenceId = createStore<number | null>(
  getPrimaryRegionIdFromStorage()
)

sample({
  clock: init,
  source: $preferenceStorage,
  fn: (storage) =>
    storage?.primaryRegion?.id ?? getPrimaryRegionIdFromStorage(),
  target: $previousPreferenceId,
})

sample({
  clock: $preference,
  source: $previousPreferenceId,
  filter: (previousId, preference) => {
    const newId = preference?.id ?? null
    return newId !== previousId
  },
  target: regionChanged,
})

sample({
  clock: $preference,
  fn: (preference) => preference?.id ?? null,
  target: $previousPreferenceId,
})

const checkLiveChannelsByPrimaryRegionFx: typeof api.live.base.getLiveChannelsByRegionFx =
  attach({
    effect: api.live.base.getLiveChannelsByRegionFx,
  })

export const $isPrimaryRegionLiveChannelsAvailable = createStore(false).on(
  checkLiveChannelsByPrimaryRegionFx.doneData,
  (_, data) => data?.length > 0
)

sample({
  clock: [init, $preference],
  source: $preference,
  filter: aye,
  fn: (preference) => {
    return { regionId: preference?.id }
  },
  target: checkLiveChannelsByPrimaryRegionFx,
})

sample({
  clock: regionChanged,
  target: [
    resetContentOnPreferredRegionChange,
    resetTvshowsOnPreferredRegionChange,
  ],
})

// Open popup if guest and load regions/countries for GUEST
sample({
  clock: [$isOnboardingEnabled, $isDataReady],
  source: {
    ready: $isDataReady,
    enabled: $isOnboardingEnabled,
    preferenceStorage: $preferenceStorage,
    isGuest: session.$isGuest,
  },
  filter: ({ ready, enabled, preferenceStorage, isGuest }) => {
    const shouldLoadGuestRegions =
      ready && enabled && Boolean(isGuest) && !preferenceStorage?.primaryRegion

    return shouldLoadGuestRegions
  },
  target: [setTryOpeningPopupToTrue, preferencesEntity.getOnboardingExtraFx],
})

// Open popup if authorized user and load regions/countries for AUTHORIZED USER
// (rare case - mainly has been added so that the onboarding form would appear after updating the app when the user is authorized, or
// in case if the authorized user cleaned the local storage).
// First login used to be excluded here because the Welcome form drove it; that
// form is gone with the one-step registration, so first login goes through this
// rule like any other session (OVP-3045).
sample({
  clock: [
    $isOnboardingEnabled,
    $isDataReady,
    session.$isFirstLogin,
    session.$isFirstLoginBySocialNetwork,
  ],
  source: {
    ready: $isDataReady,
    enabled: $isOnboardingEnabled,
    preferenceStorage: $preferenceStorage,
    isAuthenticated: session.$isAuthenticated,
  },
  filter: ({ ready, enabled, preferenceStorage, isAuthenticated }) => {
    const shouldLoadUsersRegions =
      ready &&
      enabled &&
      Boolean(isAuthenticated) &&
      !preferenceStorage?.primaryRegion

    return shouldLoadUsersRegions
  },
  target: [setTryOpeningPopupToTrue, preferencesEntity.getExtraFx],
})

// Open popup only if regions/countries have been loaded and landing region is global (triggers only at HOME page)
sample({
  clock: [preferencesEntity.$regions, landingRegionFeatureModel.$landingRegion],
  source: {
    shouldTryToOpenPopup: $shouldTryToOpenPopup,
    landingRegion: landingRegionFeatureModel.$landingRegion,
    preference: $preference,
  },
  filter: ({ shouldTryToOpenPopup, landingRegion, preference }, regions) => {
    return (
      shouldTryToOpenPopup &&
      Boolean(regions) &&
      Boolean(regions?.length) &&
      Boolean(landingRegion) &&
      landingRegion === GLOBAL_REGION &&
      !preference?.name
    )
  },
  target: openPreferencesPopup,
})

// Open popup only for all other pages except HOME and LIVE CATEGORIES
sample({
  clock: preferencesEntity.$regions,
  source: {
    shouldTryToOpenPopup: $shouldTryToOpenPopup,
    regions: preferencesEntity.$regions,
    pathname: router.$pathname,
  },
  filter: ({ shouldTryToOpenPopup, regions, pathname }) => {
    // can't use routeName from $route as it is not reachable from this scope
    const isNotHomeOrLiveCategoriesPage =
      pathname?.startsWith(PATH.MOVIE) ||
      pathname?.startsWith(PATH.TVSHOW) ||
      pathname?.startsWith(PATH.HASHTAG) ||
      pathname?.startsWith(PATH.FAVORITES) ||
      pathname?.startsWith(PATH.SEARCH) ||
      pathname?.startsWith(PATH.CREATOR) ||
      pathname?.startsWith(PATH.FAVORITES)
    return (
      shouldTryToOpenPopup &&
      Boolean(regions) &&
      Boolean(regions?.length) &&
      Boolean(isNotHomeOrLiveCategoriesPage)
    )
  },
  target: openPreferencesPopup,
})

// set landing region as primary region if it is not global
sample({
  clock: [preferencesEntity.$regions, landingRegionFeatureModel.$landingRegion],
  source: {
    shouldTryToOpenPopup: $shouldTryToOpenPopup,
    landingRegion: landingRegionFeatureModel.$landingRegion,
    regions: preferencesEntity.$regions,
    pathname: router.$pathname,
    preference: $preference,
  },
  filter: ({
    shouldTryToOpenPopup,
    landingRegion,
    regions,
    pathname,
    preference,
  }) => {
    return (
      shouldTryToOpenPopup &&
      Boolean(regions) &&
      Boolean(regions?.length) &&
      Boolean(landingRegion) &&
      landingRegion !== GLOBAL_REGION &&
      // as regions triggers also on settings page, we need to filter it out
      !pathname?.startsWith(PATH.SETTINGS) &&
      !preference?.name
    )
  },
  fn: ({ landingRegion, regions }) => {
    return (
      regions?.find(
        (r) => normalizeForComparison(r.alias ?? '') === landingRegion
      ) || null
    )
  },
  target: [setPreference, resetShouldTryToOpenPopup],
})

export const confirmSetPreference = createEvent<string>()
const resetPreferenceToBeSettled = createEvent()
const $preferenceToBeSettled = createStore<string>('')
  .on(confirmSetPreference, (_, regionName) => regionName)
  .reset(resetPreferenceToBeSettled)

sample({
  clock: confirmSetPreference,
  filter: session.$isAuthenticated,
  target: preferencesEntity.getExtraFx,
})

sample({
  clock: confirmSetPreference,
  filter: session.$isGuest,
  target: preferencesEntity.getOnboardingExtraFx,
})

const updateProfile = createEvent<IPrimaryRegion>()

sample({
  clock: preferencesEntity.$regions,
  source: $preferenceToBeSettled,
  filter: (preferenceToBeSettled, regions) =>
    Boolean(preferenceToBeSettled) &&
    Boolean(regions) &&
    Boolean(regions?.length),
  fn: (preferenceToBeSettled, regions) => {
    return (
      regions?.find(
        (r) => normalizeForComparison(r.alias ?? '') === preferenceToBeSettled
      ) || null
    )
  },
  target: [setPreference, resetPreferenceToBeSettled, updateProfile],
})

sample({
  clock: updateProfile,
  source: profile.$profile,
  filter: session.$isAuthenticated,
  fn: (profile, primaryRegion) => {
    return {
      ...profile,
      primaryRegion: {
        id: primaryRegion.id,
        name: primaryRegion.name,
        alias: primaryRegion.alias,
      },
    }
  },
  target: profile.setProfileFx,
})

sample({
  clock: selectEpgRegionModel.setRegionFromPopup,
  source: $preferenceStorage,
  filter: (preferenceStorage, region) => {
    return preferenceStorage?.primaryRegion?.id !== region?.id
  },
  fn: (_, region) => region,
  target: [setPreference, updateProfile],
})

// Direct EPG / See all URL: the lineup the user landed on, held until the user picks
// a region explicitly.
const regionFromUrlSentToProfile = createEvent<IPrimaryRegion>()

const $regionFromUrl = createStore<IPrimaryRegion>(noRegionSelection)
  .on(selectEpgRegionModel.setRegionFromUrl, (current, region) =>
    region?.id
      ? { id: region.id, name: region.name, alias: region.alias }
      : current
  )
  .reset(selectEpgRegionModel.setRegionFromPopup)

// Whether that lineup already went to the profile, so a profile that comes back
// without it does not start an endless save loop.
const $isRegionFromUrlSent = createStore(false)
  .on(regionFromUrlSentToProfile, T)
  .reset(
    selectEpgRegionModel.setRegionFromUrl,
    selectEpgRegionModel.setRegionFromPopup
  )

// The region the user picked as a guest, kept for the write at the bottom of this
// file. Declared before the rule below so it wins the shared clocks and reads the
// preference before that rule can put "Not selected" over it: on a cold social
// return the profile can land before Home `init` reports `?isFirstLogin`, and by
// then the storage is already cleared.
const $guestPickedRegion = createStore<IPrimaryRegion | null>(null).reset(
  api.user.logOutFx.done
)

sample({
  clock: [profile.$profile, $isDataReady, $isOnboardingEnabled],
  source: $preference,
  filter: (preference) => Boolean(preference?.id),
  target: $guestPickedRegion,
})

// Update preference storage with primary region from profile (region from profile is priority)
// If is first login after registration, do not update preference storage with null from profile (set preference from local storage to profile in this case)
sample({
  clock: [profile.$profile, $isDataReady, $isOnboardingEnabled],
  source: {
    ready: $isDataReady,
    enabled: $isOnboardingEnabled,
    profile: profile.$profile,
    regionFromUrl: $regionFromUrl,
    isFirstLogin: session.$isFirstLogin,
    isFirstLoginBySocialNetwork: session.$isFirstLoginBySocialNetwork,
  },
  filter: ({
    ready,
    enabled,
    profile,
    regionFromUrl,
    isFirstLogin,
    isFirstLoginBySocialNetwork,
  }) => {
    return (
      ready &&
      enabled &&
      Boolean(profile) &&
      !isFirstLogin &&
      !isFirstLoginBySocialNetwork &&
      // A profile without a region must not clear the lineup the user just opened
      // from a URL, otherwise the "Not selected" sentinel comes back.
      (Boolean(profile?.primaryRegion?.id) || !regionFromUrl.id)
    )
  },
  fn: ({ profile }) => {
    const { id, name, alias } = profile?.primaryRegion ?? {}
    let region = noRegionSelection

    if (id) {
      region = {
        id,
        name: name || '',
        alias: alias || GLOBAL_REGION,
      }
    }
    return region
  },
  target: setPreferenceWithoutUtm,
})

// Promote the URL lineup while the user still has no real region (empty storage or the
// "Not selected" sentinel). Declared after the sample above so it also wins on the
// shared clocks.
sample({
  clock: [
    selectEpgRegionModel.setRegionFromUrl,
    profile.$profile,
    $isDataReady,
    $isOnboardingEnabled,
  ],
  source: { region: $regionFromUrl, preference: $preference },
  filter: ({ region, preference }) => Boolean(region.id) && !preference?.id,
  fn: ({ region }) => region,
  target: setPreference,
})

// Same write for signed-in users. It waits for a loaded profile because updateProfile
// spreads $profile into the request, and spreading null POSTs blank firstName,
// lastName, birthday and gender.
sample({
  clock: [selectEpgRegionModel.setRegionFromUrl, profile.$profile],
  source: {
    region: $regionFromUrl,
    isSent: $isRegionFromUrlSent,
    loadedProfile: profile.$profile,
  },
  filter: ({ region, isSent, loadedProfile }) =>
    Boolean(region.id) &&
    !isSent &&
    Boolean(loadedProfile) &&
    !loadedProfile?.primaryRegion?.id,
  fn: ({ region }) => region,
  target: [updateProfile, regionFromUrlSentToProfile],
})

// Registration used to end with the Welcome form, and that form POSTed the region the
// user had picked as a guest onto the fresh profile. The one-step sign up dropped the
// form (OVP-3045), so the write happens here instead: without it the profile stays
// regionless, and the next load takes the "profile is priority" rule above and puts
// "Not selected" over the lineup the user was watching before they registered.
const storedRegionSentToProfile = createEvent()

// When the profile won the race, the rule above already replaced the region with
// "Not selected". The profile now carries it again, but that same rule skips while a
// first login is in progress, so put it back on the storage here.
const guestRegionRestored = createEvent<IPrimaryRegion>()

sample({
  clock: guestRegionRestored,
  source: $preference,
  filter: (preference, region) => preference?.id !== region.id,
  fn: (_, region) => region,
  target: setPreferenceWithoutUtm,
})

// Same guard as the EPG URL write: a profile that comes back without the region must
// not start an endless save loop.
const $isStoredRegionSent = createStore(false)
  .on(storedRegionSentToProfile, T)
  .reset(api.user.logOutFx.done)

// Clocked on the first-login stores as well: on a social return they are filled from
// Home `init`, which can run after the profile has already arrived.
sample({
  clock: [
    profile.$profile,
    $isDataReady,
    $isOnboardingEnabled,
    session.$isFirstLogin,
    session.$isFirstLoginBySocialNetwork,
  ],
  source: {
    ready: $isDataReady,
    enabled: $isOnboardingEnabled,
    region: $guestPickedRegion,
    isSent: $isStoredRegionSent,
    loadedProfile: profile.$profile,
    isFirstLogin: session.$isFirstLogin,
    isFirstLoginBySocialNetwork: session.$isFirstLoginBySocialNetwork,
  },
  filter: ({
    ready,
    enabled,
    region,
    isSent,
    loadedProfile,
    isFirstLogin,
    isFirstLoginBySocialNetwork,
  }) =>
    ready &&
    enabled &&
    !isSent &&
    (isFirstLogin || Boolean(isFirstLoginBySocialNetwork)) &&
    Boolean(region?.id) &&
    Boolean(loadedProfile) &&
    !loadedProfile?.primaryRegion?.id,
  fn: ({ region }) => ({
    id: region?.id ?? 0,
    name: region?.name ?? '',
    alias: region?.alias,
  }),
  target: [updateProfile, storedRegionSentToProfile, guestRegionRestored],
})
